import React, { useState, useEffect } from "react";
import * as APIModels from "../../../../models";
import {
    ProcessServerService,
    AttorneyProcessServerService,
} from "../../../../services/ClientService";

export default function ProcessServerAssignment({
    attorney,
}: {
    attorney: APIModels.Attorney;
}) {
    const [processServers, setProcessServers] = useState(
        [] as APIModels.ProcessServer[]
    );
    const [currentAssignment, setCurrentAssignment] =
        useState<APIModels.AttorneyProcessServer | null>(null);
    const [selectedProcessServerId, setSelectedProcessServerId] =
        useState<string>("");
    const [path, setPath] = useState<string>("");
    const [instructions, setInstructions] = useState<string>("");
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Fetch active process servers on component mount
    useEffect(() => {
        ProcessServerService.getList({ active: true }).then(
            (resp) => {
                if (resp.successful && resp.data) {
                    setProcessServers(resp.data);
                } else {
                    setProcessServers([]);
                }
            },
            (error) => {
                console.log("Error fetching process servers:", error);
                setProcessServers([]);
            }
        );
    }, []);

    // Fetch current assignment when attorney changes
    useEffect(() => {
        if (attorney.id) {
            AttorneyProcessServerService.getByAttorney({
                attorneyId: attorney.id,
            }).then(
                (resp) => {
                    if (resp.successful && resp.data) {
                        setCurrentAssignment(resp.data);
                        setSelectedProcessServerId(
                            resp.data.processServerID || ""
                        );
                        setPath(resp.data.path || "");
                        setIsEnabled(true);
                    } else {
                        setCurrentAssignment(null);
                        setSelectedProcessServerId("");
                        setPath("");
                        setIsEnabled(false);
                    }
                },
                (error) => {
                    console.log(
                        "Error fetching attorney process server:",
                        error
                    );
                    setCurrentAssignment(null);
                    setSelectedProcessServerId("");
                    setPath("");
                    setIsEnabled(false);
                }
            );
        }
    }, [attorney.id]);

    const handleEnableProcessService = () => {
        setIsEnabled(true);
        setErrorMessage("");
    };

    const handleDisableProcessService = () => {
        if (!currentAssignment?.id) return;

        setLoading(true);
        setErrorMessage("");

        AttorneyProcessServerService.delete({ id: currentAssignment.id }).then(
            (resp) => {
                setLoading(false);
                if (resp.successful) {
                    setCurrentAssignment(null);
                    setSelectedProcessServerId("");
                    setPath("");
                    setIsEnabled(false);
                } else {
                    setErrorMessage("Failed to disable process service");
                }
            },
            (error) => {
                setLoading(false);
                setErrorMessage(
                    "An error occurred while disabling process service"
                );
                console.log("Error disabling process service:", error);
            }
        );
    };

    const saveAssignment = () => {
        if (!selectedProcessServerId) return;

        setLoading(true);
        setErrorMessage("");

        const assignmentData: APIModels.AttorneyProcessServer = {
            attorneyID: attorney.id,
            processServerID: selectedProcessServerId,
            price: currentAssignment?.price,
            path: path,
            instructions: instructions,
            active: true,
        };

        if (currentAssignment?.id) {
            // Update existing assignment
            AttorneyProcessServerService.update({
                id: currentAssignment.id,
                body: {
                    ...assignmentData,
                    id: currentAssignment.id,
                },
            }).then(
                (resp) => {
                    setLoading(false);
                    if (resp.successful && resp.data) {
                        setCurrentAssignment(resp.data);
                    } else {
                        setErrorMessage(
                            "Failed to update process server assignment"
                        );
                    }
                },
                (error) => {
                    setLoading(false);
                    setErrorMessage(
                        "An error occurred while updating process server assignment"
                    );
                    console.log("Error updating assignment:", error);
                }
            );
        } else {
            // Create new assignment
            AttorneyProcessServerService.add({ body: assignmentData }).then(
                (resp) => {
                    setLoading(false);
                    if (resp.successful && resp.data) {
                        setCurrentAssignment(resp.data);
                    } else {
                        setErrorMessage(
                            "Failed to create process server assignment"
                        );
                    }
                },
                (error) => {
                    setLoading(false);
                    setErrorMessage(
                        "An error occurred while creating process server assignment"
                    );
                    console.log("Error creating assignment:", error);
                }
            );
        }
    };

    const handleProcessServerChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const processServerId = event.target.value;
        setSelectedProcessServerId(processServerId);
        setErrorMessage("");

        if (processServerId) {
            saveAssignment();
        }
    };

    return (
        <div className="card text-left">
            <div className="card-body">
                <h4 className="mb-3 bg-light p-2">Process Server Assignment</h4>

                <div className="row mb-3">
                    <div className="col-sm-6">
                        <div className="mb-3">
                            {!isEnabled ? (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleEnableProcessService}
                                    disabled={loading}
                                >
                                    Enable Process Service
                                </button>
                            ) : (
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleDisableProcessService}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Disabling...
                                        </>
                                    ) : (
                                        "Disable Process Service"
                                    )}
                                </button>
                            )}
                        </div>

                        {isEnabled && (
                            <>
                                <div className="mb-3">
                                    <label
                                        htmlFor="processServerSelect"
                                        className="form-label"
                                    >
                                        Select Process Server
                                    </label>
                                    <select
                                        className="form-select"
                                        id="processServerSelect"
                                        value={selectedProcessServerId}
                                        onChange={handleProcessServerChange}
                                        disabled={loading}
                                    >
                                        <option value="">
                                            -- Select a Process Server --
                                        </option>
                                        {processServers.map((server) => (
                                            <option
                                                key={server.id}
                                                value={server.id}
                                            >
                                                {server.companyName}{" "}
                                                {server.contactName
                                                    ? `(${server.contactName})`
                                                    : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label
                                        htmlFor="priceInput"
                                        className="form-label"
                                    >
                                        Price For Service
                                    </label>

                                    <div className="input-group">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            id="priceInput"
                                            value={
                                                currentAssignment?.price ?? 0.0
                                            }
                                            onChange={(e) =>
                                                setCurrentAssignment({
                                                    ...currentAssignment,
                                                    price: parseFloat(
                                                        e.target.value
                                                    ),
                                                })
                                            }
                                            disabled={loading}
                                            placeholder="Enter a price for service"
                                        />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label
                                        htmlFor="pathInput"
                                        className="form-label"
                                    >
                                        Path
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="pathInput"
                                            value={path}
                                            onChange={(e) =>
                                                setPath(e.target.value)
                                            }
                                            disabled={loading}
                                            placeholder="Enter file path (optional)"
                                        />
                                        {selectedProcessServerId && (
                                            <button
                                                className="btn btn-outline-primary"
                                                type="button"
                                                onClick={saveAssignment}
                                                disabled={loading}
                                                title="Save path changes"
                                            >
                                                <i className="mdi mdi-content-save"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {loading && (
                                    <div className="mb-3">
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Saving...
                                    </div>
                                )}
                            </>
                        )}

                        {currentAssignment &&
                            currentAssignment.processServer && (
                                <div className="alert alert-info">
                                    <strong>Current Assignment:</strong>{" "}
                                    {
                                        currentAssignment.processServer
                                            .companyName
                                    }
                                    {currentAssignment.processServer
                                        .contactName && (
                                        <span>
                                            {" "}
                                            (
                                            {
                                                currentAssignment.processServer
                                                    .contactName
                                            }
                                            )
                                        </span>
                                    )}
                                    {currentAssignment.path && (
                                        <div>
                                            <strong>Path:</strong>{" "}
                                            {currentAssignment.path}
                                        </div>
                                    )}
                                </div>
                            )}

                        {errorMessage && (
                            <div className="alert alert-danger">
                                {errorMessage}
                            </div>
                        )}
                    </div>
                    <div className="col-sm-6">
                        <div className="mb-3">
                            <label
                                htmlFor="instructionInput"
                                className="form-label"
                            >
                                Instructions
                            </label>
                            <div className="input-group">
                                <textarea
                                    rows={10}
                                    className="form-control"
                                    id="instructionInput"
                                    value={currentAssignment?.instructions}
                                    onChange={(e) =>
                                        setInstructions(e.target.value)
                                    }
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
